import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./verification/verification.css";

const EmailVerification = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState("Verifying your account and processing payment...");
  const [errorDetails, setErrorDetails] = useState(null);
  const [chargeDetails, setChargeDetails] = useState(null);

  useEffect(() => {
    verifyEmail();
  }, []);

  const verifyEmail = async () => {
    try {
      // Extract token from URL path (e.g., /verification/eyJhbGc...)
      const pathParts = window.location.pathname.split("/");
      const token = pathParts[pathParts.length - 1];

      if (!token || token === "verification") {
        setStatus("error");
        setMessage("Invalid verification link");
        setErrorDetails({
          error: "INVALID_LINK",
          userMessage: "The verification link is invalid. Please check your email and click the correct link."
        });
        return;
      }

      // Call backend verification endpoint
      const response = await fetch(
        `${process.env.REACT_APP_BASICSERVICE}/api/brokerages/verify-brokerage/${token}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ frontEndOrigin: window.location.origin }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Success - account verified and charged
        setStatus("success");
        setMessage("Account verified successfully! Payment processed.");
        setChargeDetails(data.chargeDetails);
        toast.success(data.status || "Verification successful!");

        // Redirect to homepage after 3 seconds
        setTimeout(() => {
          navigate("/");
        }, 3000);
      } else {
        // Error occurred
        setStatus("error");
        setErrorDetails(data);

        // Set user-friendly message based on error type
        if (data.error === "ALREADY_VERIFIED") {
          setMessage("Account already verified");
          toast.info("Your account is already verified. Please login.");
          setTimeout(() => {
            navigate("/");
          }, 2000);
        } else if (data.error === "TOKEN_EXPIRED") {
          setMessage("Verification link expired");
          toast.error("Your verification link has expired. A new one has been sent to your email.");
        } else if (data.error === "INVALID_TOKEN") {
          setMessage("Invalid verification link");
          toast.error("The verification link is invalid. Please check your email.");
        } else if (data.error === "NO_PAYMENT_METHOD") {
          setMessage("Payment method not found");
          toast.error("No payment method found. Please contact support.");
        } else if (data.error === "CARD_DECLINED" || data.error === "INSUFFICIENT_FUNDS") {
          setMessage("Payment failed");
          toast.error(data.userMessage || "Payment could not be processed.");
        } else {
          setMessage("Verification failed");
          toast.error(data.userMessage || data.message || "An error occurred during verification.");
        }
      }
    } catch (error) {
      console.error("Verification error:", error);
      setStatus("error");
      setMessage("Network error");
      setErrorDetails({
        error: "NETWORK_ERROR",
        userMessage: "Could not connect to the server. Please check your internet connection and try again."
      });
      toast.error("Network error. Please try again.");
    }
  };

  const renderContent = () => {
    if (status === "loading") {
      return (
        <div className="verification-container">
          <div className="verification-spinner"></div>
          <h2>Verifying Your Account</h2>
          <p className="verification-subtext">Please wait while we process your verification...</p>
        </div>
      );
    }

    if (status === "success") {
      return (
        <div className="verification-container success">
          <div className="verification-icon success-icon">✓</div>
          <h2>Account Verified!</h2>
          <p>Your account has been successfully verified and activated.</p>
          <p className="verification-redirect">Redirecting to login page...</p>
        </div>
      );
    }

    if (status === "error") {
      return (
        <div className="verification-container error">
          <div className="verification-icon error-icon">✕</div>
          <h2>Something Went Wrong</h2>
          <p className="error-message">We were unable to verify your account.</p>
          <p>Please contact Quotey support at <a href="mailto:info@quotey.io">info@quotey.io</a> for assistance.</p>

          <div className="action-buttons">
            <button
              className="btn-primary"
              onClick={() => navigate("/")}
            >
              Return to Home
            </button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="email-verification-page">
      {renderContent()}
    </div>
  );
};

export default EmailVerification;
